// src/components/loadletter/templateEditorExtensions.ts
//
// TipTap extensions for the load letter template editor (issue #282).

import { Extension, Node, mergeAttributes } from '@tiptap/react';
import { FIELD_ATTR, fieldLabel, isKnownField } from './templateFields';

/**
 * A personalised field such as {{staffName}}, shown as a labelled tag. It is an
 * atom: it can be inserted and deleted whole, never edited or half-deleted, so
 * an admin can't leave a broken `{{staffNa` behind.
 */
export const TemplateFieldNode = Node.create({
  name: 'templateField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      field: {
        default: '',
        parseHTML: el => el.getAttribute(FIELD_ATTR) ?? '',
        renderHTML: attrs => ({ [FIELD_ATTR]: attrs.field }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[${FIELD_ATTR}]` }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', HTMLAttributes, `{{${node.attrs.field}}}`];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('span');
      const key = node.attrs.field as string;
      dom.className = isKnownField(key) ? 'template-field-chip' : 'template-field-chip template-field-chip--unknown';
      dom.textContent = isKnownField(key) ? fieldLabel(key) : `Unknown field: ${key}`;
      dom.title = `Filled in for each staff member ({{${key}}})`;
      dom.contentEditable = 'false';
      return { dom };
    };
  },
});

/**
 * A <div> with its own styling, e.g. the yellow highlighted meeting schedule box
 * in the default template. Without this the editor would flatten such boxes into
 * plain paragraphs and silently drop their styling on the first save.
 */
export const StyledBlockNode = Node.create({
  name: 'styledBlock',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      style: { default: null, parseHTML: el => el.getAttribute('style'), renderHTML: a => (a.style ? { style: a.style } : {}) },
      class: { default: null, parseHTML: el => el.getAttribute('class'), renderHTML: a => (a.class ? { class: a.class } : {}) },
    };
  },

  parseHTML() {
    return [{ tag: 'div' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

const withoutTextAlign = (style: string | null): string | null => {
  if (!style) return null;
  const kept = style
    .split(';')
    .map(s => s.trim())
    .filter(s => s && !/^text-align\s*:/i.test(s))
    .join('; ');
  return kept || null;
};

/**
 * Keep existing inline styles on paragraphs, headings and dividers (e.g. the
 * gold rule and small-print paragraph in the default template). text-align is
 * left to the TextAlign extension so the alignment buttons stay in charge of it.
 */
export const PreservedStyles = Extension.create({
  name: 'preservedStyles',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'horizontalRule'],
        attributes: {
          preservedStyle: {
            default: null,
            parseHTML: el => withoutTextAlign(el.getAttribute('style')),
            renderHTML: attrs => (attrs.preservedStyle ? { style: attrs.preservedStyle } : {}),
          },
        },
      },
    ];
  },
});
